import type { ReactNode } from 'react';
import { Fragment } from 'react';

import { cn } from '@april9au/react-common/utils';

import type { ChatMessage } from '@/chat/types';
import type { Buddy } from '@/store/user';
import { Bubble, BubbleContent, BubbleGroup } from '@/components/ui/bubble';
import { Icon } from '@/components/ui/icon';
import { Marker, MarkerContent } from '@/components/ui/marker';
import { Message, MessageAvatar, MessageContent, MessageHeader } from '@/components/ui/message';
import { MessageScrollerItem } from '@/components/ui/message-scroller';
import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';
import { ActivityCard } from './activities/activity-card';
import { BuddyAvatar, CoachAvatar, TypingDots, useCoachName, useShowsBuddy } from './buddy-avatar';
import { CompletionCard, TakeawaysCard } from './completion-card';
import { CarouselMessage, ImageMessage } from './media-messages';
import { PlainText, RichText } from './rich-text';
import { VideoMessage } from './video-message';

/**
 * The renderer registry: one entry per message kind the reducer can land.
 *
 * Adding a message kind means adding an entry here — the `satisfies` below
 * makes a missing one a type error, which is the hook ticket 07 needs for
 * `activity`.
 */

export type ChatRenderContext = {
  buddy: Buddy;
  /** True when finishing this module finishes the course. */
  courseComplete: boolean;
  /** The step id of the video currently gating the chat, if any. */
  gateStepId: string | null;
  /**
   * Whether the gating video's player is open. The chat owns it because the
   * answer footer opens it too — see `VideoMessage`.
   */
  gateOpen: boolean;
  onGateOpenChange: (open: boolean) => void;
  /** Passes the gate. Only the gating video's card shows a Continue button. */
  onGateContinue: () => void;
};

export type MessageRenderer<K extends ChatMessage['kind']> = (props: {
  message: Extract<ChatMessage, { kind: K }>;
  context: ChatRenderContext;
}) => ReactNode;

/** Bubble kinds are grouped by `ChatThread`, so they are not in the registry. */
type StandaloneKind = Exclude<ChatMessage['kind'], 'coach' | 'user' | 'feedback'>;

export const messageRenderers = {
  image: ({ message }) => <ImageMessage image={message.image} />,

  carousel: ({ message }) => <CarouselMessage images={message.images} />,

  video: ({ message, context }) => {
    // Only the video the chat is waiting on is gating, and only that one hands
    // its player state to the chat; every other video keeps its own.
    const gating = context.gateStepId === message.id;

    return (
      <VideoMessage
        title={message.title}
        subtitle={message.subtitle}
        poster={message.poster}
        embedLink={message.embedLink}
        transcript={message.transcript}
        onContinue={gating ? context.onGateContinue : undefined}
        open={gating ? context.gateOpen : undefined}
        onOpenChange={gating ? context.onGateOpenChange : undefined}
      />
    );
  },

  marker: ({ message }) => (
    <Marker variant="separator">
      <MarkerContent className="text-chat-ink text-[11px] font-bold tracking-[0.14em] uppercase">
        {message.index}. <PlainText field={message.title} />
      </MarkerContent>
    </Marker>
  ),

  /**
   * A discovery's running count. A separator rather than a bubble: it is the
   * thread keeping score, not the coach saying something, and it sits between
   * one entry and the next choice exactly where a checkpoint marker would.
   */
  discovered: ({ message }) => (
    <Marker variant="separator">
      <MarkerContent className="text-chat-ink-muted flex items-center gap-1.5 text-[11px] font-bold tracking-[0.12em] uppercase">
        <Icon name="passport/check-circle" aria-hidden className="text-answer-correct size-3.5" />
        {message.text}
      </MarkerContent>
    </Marker>
  ),

  takeaways: ({ message }) => <TakeawaysCard heading={message.heading} points={message.points} />,

  completion: ({ message, context }) => (
    <CompletionCard
      score={message.score}
      moduleIndex={message.moduleIndex}
      moduleTotal={message.moduleTotal}
      upNext={message.upNext}
      courseComplete={context.courseComplete}
    />
  ),

  activity: ({ message }) => <ActivityCard message={message} />,
} satisfies { [K in StandaloneKind]: MessageRenderer<K> };

/**
 * What the learner tapped, in words. A picture answer whose asset carries no
 * alt text has none, so it is named by its position — the same name the chip
 * it came from announced.
 */
function UserEcho({ text, optionNumber }: { text: string; optionNumber?: number }) {
  const { chat } = useStrings();

  if (text !== '') {
    return <>{text}</>;
  }

  return <>{optionNumber === undefined ? null : formatString(chat.chat_option_number, { number: optionNumber })}</>;
}

/** One coach or feedback bubble inside a group. */
function CoachBubble({ message, isLast }: { message: ChatMessage; isLast: boolean }) {
  if (message.kind === 'coach') {
    return (
      <Bubble variant="buddy">
        <BubbleContent
          className={cn(
            'rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed',
            isLast && 'rounded-es-md',
            // Bold the question here so editors need not bold every prompt in Prismic.
            message.prompt === true && 'font-semibold',
          )}
        >
          <RichText field={message.text} />
        </BubbleContent>
      </Bubble>
    );
  }

  if (message.kind === 'feedback') {
    return (
      <Bubble variant="buddy">
        {/*
          The rule is an element, not a border. `bubbleVariants` styles the
          content through a parent selector (`*:data-[slot=bubble-content]`),
          which out-specifies any border utility passed as a className — the
          trap ticket 01 flagged. A 4 px bar inside the bubble is immune.
        */}
        <span
          aria-hidden
          className={cn(
            'absolute inset-y-0 start-0 z-10 w-1 rounded-s-2xl',
            isLast && 'rounded-es-md',
            message.correct ? 'bg-answer-correct' : 'bg-answer-wrong',
          )}
        />
        <BubbleContent
          className={cn('rounded-2xl py-2.5 ps-5 pe-4 text-[15px] leading-relaxed', isLast && 'rounded-es-md')}
        >
          {/*
            The rule is the only thing that says right or wrong, and it is
            `aria-hidden` colour. Authored feedback rarely says so in words
            ("You can find patrol hours at ..."), so the verdict is spoken
            here — read out by the thread's own live region, once.
          */}
          {message.text === null ? null : <Verdict correct={message.correct} />}
          {message.text === null ? (
            <FallbackFeedback
              correct={message.correct}
              reveal={message.kind === 'feedback' && message.reveal === true}
              hint={message.kind === 'feedback' && message.hint === true}
            />
          ) : (
            <RichText field={message.text} />
          )}
        </BubbleContent>
      </Bubble>
    );
  }

  return null;
}

/** The verdict in words, for a reader who cannot see the coloured rule. */
function Verdict({ correct }: { correct: boolean }) {
  const { chat } = useStrings();

  return <span className="sr-only">{correct ? chat.chat_correct : chat.chat_incorrect}. </span>;
}

/**
 * No authored feedback: the catalogue says right or wrong instead. `reveal` is
 * the bubble that hands over an activity's answer, which is a different thing
 * to say than "not quite" — the answer is on the card below it. `hint` is a
 * repeat miss on an activity, and points the learner to the card's hint.
 */
function FallbackFeedback({
  correct,
  reveal = false,
  hint = false,
}: {
  correct: boolean;
  reveal?: boolean;
  hint?: boolean;
}) {
  const { chat } = useStrings();

  return (
    <span className={cn('font-semibold', correct ? 'text-answer-correct' : 'text-answer-wrong')}>
      {reveal ? chat.chat_reveal : correct ? chat.chat_correct : hint ? chat.chat_incorrect_hint : chat.chat_incorrect}
    </span>
  );
}

/** Consecutive coach-side messages share one avatar and one group. */
function groupMessages(messages: Array<ChatMessage>): Array<Array<ChatMessage>> {
  const groups: Array<Array<ChatMessage>> = [];
  const isCoachSide = (message: ChatMessage) => message.kind === 'coach' || message.kind === 'feedback';

  for (const message of messages) {
    const last = groups.at(-1);

    const head = last?.[0];

    if (last && head && isCoachSide(message) && isCoachSide(head)) {
      last.push(message);
    } else {
      groups.push([message]);
    }
  }

  return groups;
}

export type ChatThreadProps = {
  messages: Array<ChatMessage>;
  typing: boolean;
  context: ChatRenderContext;
};

/**
 * The coach's name over each run of coach messages. It is also the accessible
 * name, so the avatar beside it is `aria-hidden`.
 */
function CoachName() {
  return <MessageHeader className="text-chat-ink px-1 text-xs font-semibold">{useCoachName()}</MessageHeader>;
}

export function ChatThread({ messages, typing, context }: ChatThreadProps) {
  const showsBuddy = useShowsBuddy();

  return (
    <>
      {groupMessages(messages).map((group) => {
        const first = group[0];

        if (!first) {
          return null;
        }

        if (first.kind === 'coach' || first.kind === 'feedback') {
          return (
            <MessageScrollerItem key={first.id} messageId={first.id} className="chat-message-in">
              <Message>
                <MessageAvatar aria-hidden className="size-9 self-end overflow-visible bg-transparent">
                  <CoachAvatar />
                </MessageAvatar>
                <MessageContent className="gap-1">
                  <CoachName />
                  <BubbleGroup>
                    {group.map((message, index) => (
                      <Fragment key={message.id}>
                        <CoachBubble message={message} isLast={index === group.length - 1} />
                      </Fragment>
                    ))}
                  </BubbleGroup>
                </MessageContent>
              </Message>
            </MessageScrollerItem>
          );
        }

        if (first.kind === 'user') {
          return (
            <MessageScrollerItem key={first.id} messageId={first.id} className="chat-message-in chat-message-in-end">
              <Message align="end">
                {/* On the kids path the learner's own character answers, facing
                    the coach. First in the DOM, so `align="end"` puts it outermost. */}
                {showsBuddy ? (
                  <MessageAvatar aria-hidden className="size-9 self-end overflow-visible bg-transparent">
                    <BuddyAvatar buddy={context.buddy} />
                  </MessageAvatar>
                ) : null}
                <MessageContent>
                  <Bubble variant="user" align="end">
                    <BubbleContent className="rounded-2xl rounded-ee-md px-4 py-2.5 text-[15px]">
                      <UserEcho text={first.text} optionNumber={first.optionNumber} />
                    </BubbleContent>
                  </Bubble>
                </MessageContent>
              </Message>
            </MessageScrollerItem>
          );
        }

        return (
          <MessageScrollerItem key={first.id} messageId={first.id} className="chat-message-in">
            <StandaloneMessage message={first} context={context} />
          </MessageScrollerItem>
        );
      })}

      {typing ? (
        <MessageScrollerItem messageId="typing" className="chat-message-in">
          <Message>
            <MessageAvatar aria-hidden className="size-9 self-end overflow-visible bg-transparent">
              <CoachAvatar />
            </MessageAvatar>
            <MessageContent>
              <Bubble variant="buddy">
                <BubbleContent className="rounded-2xl rounded-es-md px-4 py-3">
                  <TypingDots />
                </BubbleContent>
              </Bubble>
            </MessageContent>
          </Message>
        </MessageScrollerItem>
      ) : null}
    </>
  );
}

/**
 * Dispatches through the registry. The cast is the one place the union is
 * re-joined: `messageRenderers` is keyed by kind, so the lookup is correct by
 * construction but not provable to TypeScript.
 */
function StandaloneMessage({ message, context }: { message: ChatMessage; context: ChatRenderContext }) {
  const render = messageRenderers[message.kind as StandaloneKind] as MessageRenderer<StandaloneKind>;

  return (
    <div
      className={cn(
        'flex w-full',
        // Media and cards sit on the coach's side, indented past the avatar.
        message.kind === 'marker' || message.kind === 'discovered' || message.kind === 'completion' ? 'ps-0' : 'ps-11',
      )}
    >
      {render({ message: message as Extract<ChatMessage, { kind: StandaloneKind }>, context })}
    </div>
  );
}
